// ✅ src/pages/VehicleV2/DieselConsumptionReport_Print.jsx
// Print page + client-side PDF download
// KM  -> GET /api/dieselConsumption/kmConsumption
// HR  -> GET /api/dieselConsumption/hrConsumption

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa6";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "https://nissi-india.com/api/dieselConsumption";

const toStr = (v) => String(v ?? "").trim();

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const normalizeBranch = (v) => {
  const s = toStr(v);
  if (!s || isSelect(s)) return "";
  if (/^\d+$/.test(s)) return s;
  const m = s.match(/(\d+)/);
  return m ? m[1] : "";
};

const pdfEscape = (s) =>
  String(s ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)")
    .replace(/\r?\n/g, " ");

function downloadPdfFromRows({ title, filters, rows, getters }) {
  const pageW = 842;
  const pageH = 595;
  const margin = 28;
  const lineH = 12;
  const colX = [28, 58, 128, 188, 278, 348, 398, 448, 518, 558, 598, 638, 688];

  const header = [
    "SNo",
    "Reg No",
    "Code",
    "Name",
    "Branch",
    "Type",
    "Basis",
    "Date",
    "KM",
    "HR",
    "Qty",
    "Rate",
    "Amt",
  ];

  const bodyRows = (Array.isArray(rows) ? rows : []).map((r, idx) => [
    String(idx + 1),
    getters.getRegNo(r),
    getters.getVehicleCode(r),
    getters.getVehicleName(r),
    getters.getBranch(r),
    getters.getVehType(r),
    getters.getBasis(r),
    getters.getDate(r) || "-",
    getters.getKm(r) || "-",
    getters.getHr(r) || "-",
    getters.getDieselQty(r) || "-",
    getters.getDieselRate(r) || "-",
    getters.getAmount(r) || "-",
  ]);

  const linesPerPage = Math.floor((pageH - 90) / lineH);
  const pages = [];
  let start = 0;
  while (start < bodyRows.length || pages.length === 0) {
    pages.push(bodyRows.slice(start, start + linesPerPage));
    start += linesPerPage;
    if (!bodyRows.length) break;
  }

  const objects = [];
  const addObj = (s) => {
    objects.push(s);
    return objects.length;
  };

  addObj("<< /Type /Catalog /Pages 2 0 R >>");
  const pageIds = [];

  const makePageContent = (pageRows, pageNo, pageCount) => {
    const cmds = [];
    cmds.push("BT");
    cmds.push("/F1 14 Tf");
    cmds.push(`1 0 0 1 ${margin} ${pageH - 28} Tm`);
    cmds.push(`(${pdfEscape(title)}) Tj`);
    cmds.push("/F1 8 Tf");
    cmds.push(`1 0 0 1 ${margin} ${pageH - 44} Tm`);
    cmds.push(
      `(${pdfEscape(
        `Type: ${filters.Type || "-"}  Branch: ${filters.branch || "-"}  Month/Year: ${filters.month || "-"}/${filters.year || "-"}  From: ${filters.fromdate || "-"}  To: ${filters.todate || "-"}`
      )}) Tj`
    );

    let y = pageH - 66;
    header.forEach((h, i) => {
      cmds.push("/F1 8 Tf");
      cmds.push(`1 0 0 1 ${colX[i]} ${y} Tm`);
      cmds.push(`(${pdfEscape(h)}) Tj`);
    });
    y -= lineH;

    pageRows.forEach((row) => {
      row.forEach((cell, i) => {
        cmds.push("/F1 7 Tf");
        cmds.push(`1 0 0 1 ${colX[i]} ${y} Tm`);
        cmds.push(`(${pdfEscape(String(cell).slice(0, 18))}) Tj`);
      });
      y -= lineH;
    });

    cmds.push("/F1 8 Tf");
    cmds.push(`1 0 0 1 ${margin} 20 Tm`);
    cmds.push(`(${pdfEscape(`Page ${pageNo} of ${pageCount}`)}) Tj`);
    cmds.push("ET");
    return cmds.join("\n");
  };

  const contentIds = [];
  pages.forEach((pageRows, idx) => {
    const stream = makePageContent(pageRows, idx + 1, pages.length);
    const id = addObj(
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`
    );
    contentIds.push(id);
  });

  pages.forEach((_, idx) => {
    const id = addObj(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] /Contents ${contentIds[idx]} 0 R /Resources << /Font << /F1 3 0 R >> >> >>`
    );
    pageIds.push(id);
  });

  addObj("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");

  objects[1] = `<< /Type /Pages /Kids [${pageIds
    .map((id) => `${id} 0 R`)
    .join(" ")}] /Count ${pageIds.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((obj, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";
  offsets.slice(1).forEach((off) => {
    pdf += `${String(off).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;

  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Diesel_Consumption_${filters.Type || "Report"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function DieselConsumptionReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const commonHeaders = useMemo(() => {
    const finYearId = toStr(localStorage.getItem("finYearId"));
    const finYear = toStr(localStorage.getItem("finYear"));
    const branchId = toStr(
      localStorage.getItem("branchId") ||
        localStorage.getItem("Branchid") ||
        localStorage.getItem("branchid")
    );
    const branchType = toStr(
      localStorage.getItem("branchType") || localStorage.getItem("branchtype")
    );
    const userId = toStr(
      localStorage.getItem("userId") ||
        localStorage.getItem("loginuserid") ||
        localStorage.getItem("loginUserId")
    );
    return {
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    };
  }, []);

  const passed = useMemo(() => {
    const fromQs = {
      vehtype: qs.get("vehtype"),
      branch: qs.get("branch"),
      vehbasis: qs.get("vehbasis"),
      month: qs.get("month"),
      year: qs.get("year"),
      fromDate: qs.get("fromDate"),
      toDate: qs.get("toDate"),
      vehRegNo: qs.get("vehRegNo"),
      vehicleName: qs.get("vehicleName"),
      vehicleCode: qs.get("vehicleCode"),
      vehicleMainType: qs.get("vehicleMainType"),
      mode: qs.get("mode"),
      type: qs.get("type"),
    };
    return { ...fromQs, ...(location.state || {}) };
  }, [qs, location.state]);

  const reportType = useMemo(() => {
    const t = toStr(
      passed?.Type ?? passed?.type ?? passed?.dieselType ?? passed?.reportType
    ).toLowerCase();
    if (t === "km" || t === "k" || t === "kmwise") return "KM";
    return "HR";
  }, [passed]);

  const filters = useMemo(() => {
    const vehtype = toStr(
      passed?.dieseltype ?? passed?.vehtype ?? passed?.vehicleType ?? passed?.vehicleTypeText
    );
    const branch = normalizeBranch(
      passed?.dieselbranch ?? passed?.branch ?? passed?.branchid ?? passed?.vehiclebranch
    );
    const vehbasis = toStr(passed?.dieselbasis ?? passed?.vehbasis ?? passed?.basis);
    const month = toStr(passed?.dieselmonth ?? passed?.month);
    const year = toStr(passed?.dieselyear ?? passed?.year);
    const fromdate = toStr(passed?.dieselfromdate ?? passed?.fromdate ?? passed?.fromDate);
    const todate = toStr(passed?.dieseltodate ?? passed?.todate ?? passed?.toDate);
    const vehregno = toStr(
      passed?.dieselregno ?? passed?.vehregno ?? passed?.vehRegNo ?? passed?.regno
    );
    const vehcode = toStr(
      passed?.dieselcode ??
        passed?.vehcode ??
        passed?.vehicleCode ??
        passed?.vehiclecode ??
        passed?.fld_vehicle_code
    );
    const vehname = toStr(
      passed?.dieselname ??
        passed?.vehname ??
        passed?.vehicleName ??
        passed?.vehiclename ??
        passed?.fld_vehicle_name
    );
    const fld_vehiclemaintypeid = toStr(
      passed?.dieselVehicletype ??
        passed?.fld_vehiclemaintypeid ??
        passed?.vehicleMainType ??
        passed?.vehicleTypeId ??
        passed?.vehicletypeid
    );

    return {
      vehtype,
      branch,
      vehbasis: isSelect(vehbasis) ? "" : vehbasis,
      month,
      year,
      fromdate,
      todate,
      vehregno,
      vehcode,
      vehname,
      fld_vehiclemaintypeid: isSelect(fld_vehiclemaintypeid) ? "" : fld_vehiclemaintypeid,
      Type: reportType,
      mode: toStr(passed?.mode),
      loginuserid:
        Number(sessionStorage.getItem("loginuserid")) || Number(passed?.loginuserid) || 0,
      branchType: toStr(
        sessionStorage.getItem("branchtype") || passed?.branchType || passed?.branchtype
      ),
    };
  }, [passed, reportType]);

  const endpoint = useMemo(
    () => (reportType === "KM" ? "kmConsumption" : "hrConsumption"),
    [reportType]
  );

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      const allEmpty =
        !filters.vehtype &&
        !filters.branch &&
        !filters.vehbasis &&
        !filters.month &&
        !filters.year &&
        !filters.fromdate &&
        !filters.todate &&
        !filters.vehregno &&
        !filters.vehcode &&
        !filters.vehname &&
        !filters.fld_vehiclemaintypeid;

      if (allEmpty) {
        throw new Error("Print params missing. Open print from Diesel Consumption Report page.");
      }

      const { data } = await axios.get(`${API_BASE}/${endpoint}`, {
        params: filters,
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load report");
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getters = {
    getRegNo: (r) =>
      pick(r, ["vehregno", "fld_vehicle_regno", "vehicle_regno", "RegNo", "regno"], "-"),
    getVehicleCode: (r) =>
      pick(r, ["vehcode", "fld_vehicle_code", "vehiclecode", "veh_code"], "-"),
    getVehicleName: (r) =>
      pick(r, ["vehname", "fld_vehicle_name", "vehiclename", "veh_name"], "-"),
    getBranch: (r) =>
      pick(r, ["branch", "BranchName", "branchname", "fld_branchname"], "-"),
    getVehType: (r) =>
      pick(r, ["vehtype", "VehicleType", "vehicletype", "fld_vehicletypename"], "-"),
    getBasis: (r) =>
      pick(r, ["vehbasis", "VehicleBasis", "basis", "fld_vehiclebasis"], "-"),
    getDate: (r) =>
      pick(r, ["date", "EntryDate", "entrydate", "FillingDate", "fld_date"], ""),
    getKm: (r) => pick(r, ["km", "KM", "TotalKM", "total_km", "fld_km"], ""),
    getHr: (r) => pick(r, ["hr", "HR", "Hrs", "TotalHR", "total_hr", "fld_hr"], ""),
    getDieselQty: (r) =>
      pick(r, ["diesel_qty", "DieselQty", "fld_dieselqty", "qty", "Quantity", "Diesel"], ""),
    getDieselRate: (r) =>
      pick(r, ["diesel_rate", "DieselRate", "rate", "Rate", "fld_rate"], ""),
    getAmount: (r) =>
      pick(r, ["amount", "Amount", "fld_amount", "total_amount", "Total", "NetAmount"], ""),
  };

  const exportPdf = () => {
    try {
      setExporting(true);
      if (!rows.length) {
        toast.info("No records to export");
        return;
      }
      downloadPdfFromRows({
        title: `DIESEL CONSUMPTION REPORT (${reportType})`,
        filters,
        rows,
        getters,
      });
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err?.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const last = rows?.[rows.length - 1];
    const tQty = pick(last, ["TotalDieselQty", "total_diesel_qty", "tot_diesel_qty", "TotQty"], "");
    const tAmt = pick(last, ["TotalAmount", "total_amount", "tot_amount", "TotAmount"], "");
    return { tQty, tAmt };
  }, [rows]);

  return (
    <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
      <HeaderBar />
      <VehicleLayoutWrapper>
        <div className="w-full bg-white">
          <style>{`
            @page { size: A4 portrait; margin: 10mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? (
                "Loading..."
              ) : errMsg ? (
                <span className="text-red-600">{errMsg}</span>
              ) : (
                `Type: ${reportType} | Records: ${rows.length}`
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>

              <button
                onClick={() => window.print()}
                disabled={loading || !rows.length}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>

              <button
                onClick={exportPdf}
                disabled={exporting || loading || !rows.length}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60"
              >
                <FaFilePdf /> {exporting ? "Exporting..." : "PDF"}
              </button>
            </div>
          </div>

          <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>
                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-b border-black text-center font-bold py-2 text-sm">
                  DIESEL CONSUMPTION REPORT ({reportType})
                </div>

                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div>
                        <span className="font-semibold">Vehicle Type</span> : {filters.vehtype || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Branch</span> : {filters.branch || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Basis</span> : {filters.vehbasis || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Month / Year</span> : {filters.month || "-"} /{" "}
                        {filters.year || "-"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div>
                        <span className="font-semibold">From</span> : {filters.fromdate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">To</span> : {filters.todate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Reg No</span> : {filters.vehregno || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Vehicle</span> : {filters.vehcode || "-"} / {filters.vehname || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-2">
                  <table className="w-full border border-black border-collapse text-[12px]">
                    <thead>
                      <tr>
                        <th className="border border-black px-2 py-1">S.No</th>
                        <th className="border border-black px-2 py-1">Reg No</th>
                        <th className="border border-black px-2 py-1">Veh Code</th>
                        <th className="border border-black px-2 py-1">Vehicle Name</th>
                        <th className="border border-black px-2 py-1">Branch</th>
                        <th className="border border-black px-2 py-1">Type</th>
                        <th className="border border-black px-2 py-1">Basis</th>
                        <th className="border border-black px-2 py-1">Date</th>
                        <th className="border border-black px-2 py-1">KM</th>
                        <th className="border border-black px-2 py-1">HR</th>
                        <th className="border border-black px-2 py-1">Qty</th>
                        <th className="border border-black px-2 py-1">Rate</th>
                        <th className="border border-black px-2 py-1">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && rows.length === 0 ? (
                        <tr>
                          <td className="border border-black px-2 py-2 text-center" colSpan={13}>
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={pick(r, ["id", "fld_id", "RowId"], `${idx}`)}>
                            <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>
                            <td className="border border-black px-2 py-1">{getters.getRegNo(r)}</td>
                            <td className="border border-black px-2 py-1">{getters.getVehicleCode(r)}</td>
                            <td className="border border-black px-2 py-1">{getters.getVehicleName(r)}</td>
                            <td className="border border-black px-2 py-1">{getters.getBranch(r)}</td>
                            <td className="border border-black px-2 py-1">{getters.getVehType(r)}</td>
                            <td className="border border-black px-2 py-1">{getters.getBasis(r)}</td>
                            <td className="border border-black px-2 py-1">{getters.getDate(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getters.getKm(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getters.getHr(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getters.getDieselQty(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getters.getDieselRate(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getters.getAmount(r) || "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {rows.length > 0 && (totals.tQty || totals.tAmt) && (
                      <tfoot>
                        <tr>
                          <td className="border border-black px-2 py-1 font-semibold text-right" colSpan={10}>
                            Total
                          </td>
                          <td className="border border-black px-2 py-1 font-semibold text-right">
                            {totals.tQty || ""}
                          </td>
                          <td className="border border-black px-2 py-1 font-semibold text-right"></td>
                          <td className="border border-black px-2 py-1 font-semibold text-right">
                            {totals.tAmt || ""}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </VehicleLayoutWrapper>
    </div>
  );
}